import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Chart from 'react-apexcharts';
import { initiativesApi } from '../services/trackerApi';
import { peopleApi } from '../services/api';
import { formatDate, fmtRelative, HEALTH_COLOR, CATEGORY_COLOR, effectiveHealth, TIER_TONE, TIER_ICON, tierSlug, initials } from '../utils/format';
import { buildChildrenIndex, getRootInitiatives, getUnreachableInitiatives } from '../utils/initiativeHierarchy';
import type { Initiative, InitiativeTier } from '../types/trackerModels';

type TierFilter = InitiativeTier | 'All';
const TIER_FILTERS: TierFilter[] = ['Portfolio', 'Program', 'Project', 'All'];

/** One row of the timeline once a tier is selected — depth 0 is the row's own level for that
 *  view (every Portfolio, every Program, or every Project), depth 1+ is however far its
 *  descendants cascade below it (a Portfolio's Programs, then their Projects). The `visited`
 *  guard matters here specifically because, unlike a plain top-down tree walk, "every node of
 *  this tier is a root regardless of its own parent" can otherwise form a real cycle: two
 *  Programs that each (incorrectly) name the other as parent would recurse forever without it.
 *  It terminates safely either way, but if two same-tier nodes DO mutually reference each other
 *  this way, one of the two is silently absorbed as a nested "child" of the other rather than
 *  both showing at depth 0 - which of the two wins is just iteration order, not anything real
 *  about the data. Accepted rather than fixed: InitiativeFormPage.tsx's own parent picker
 *  already only ever offers the correct next tier up, so this specific shape (same-tier mutual
 *  parents) can't be produced through the app's own UI at all, only by direct API/table access -
 *  matching this codebase's existing precedent of no server-side cycle validation (see
 *  prod-nimbus-departments' parentDepartmentId, which accepts the identical risk). */
function flattenTierTree(roots: Initiative[], childrenOf: Map<string, Initiative[]>): { initiative: Initiative; depth: number }[] {
  const rows: { initiative: Initiative; depth: number }[] = [];
  const visited = new Set<string>();
  const visit = (node: Initiative, depth: number) => {
    if (visited.has(node.initiativeId)) return;
    visited.add(node.initiativeId);
    rows.push({ initiative: node, depth });
    (childrenOf.get(node.initiativeId) || []).forEach((child) => visit(child, depth + 1));
  };
  roots.forEach((r) => visit(r, 0));
  return rows;
}

/** Apexcharts' y-axis labels are plain SVG text - no HTML/CSS indentation - so nesting is
 *  represented with leading non-breaking spaces plus a small arrow, the same convention
 *  terminal tree views use. */
const indentLabel = (name: string, depth: number): string => (depth === 0 ? name : `${'    '.repeat(depth)}↳ ${name}`);

// Health colour tokens — real --bs-* semantic variables (not hardcoded hex) so the
// portfolio chart, progress fills, and legend all stay in sync with the theme.
const HEALTH_HEX: Record<string, string> = {
  'On Track': 'var(--bs-success)',
  'At Risk': 'var(--bs-warning)',
  Blocked: 'var(--bs-danger)',
  'Not Started': 'var(--bs-secondary)',
};

// np-tone-* uses "neutral" where the old Bootstrap colour name was "secondary";
// everything else maps straight across.
const toneOf = (color: string): string => (color === 'secondary' ? 'neutral' : color);

export const OverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: initiatives = [] } = useQuery<Initiative[]>({ queryKey: ['initiatives'], queryFn: initiativesApi.getAll });
  const { data: people = [] } = useQuery({ queryKey: ['people'], queryFn: peopleApi.getAll });
  const [groupBy, setGroupBy] = useState<'category' | 'owner'>('category');
  const [tierFilter, setTierFilter] = useState<TierFilter>('All');
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  const toggleCollapsed = (initiativeId: string) => {
    setCollapsedIds((current) => {
      const next = new Set(current);
      if (next.has(initiativeId)) next.delete(initiativeId);
      else next.add(initiativeId);
      return next;
    });
  };

  // BAU initiatives (ongoing standing work, no real end date - e.g. a recurring stand-up) are
  // deliberately kept out of the project-shaped stat tiles and timeline chart below: health/
  // on-track/at-risk and a Gantt bar both assume something with a start and an end, which an
  // UNTIERED BAU item by definition doesn't have. A BAU item that's actually been given a real
  // tier/parentInitiativeId is different - it's a recognised part of that Program's structure,
  // and excluding it here made it vanish from its parent's tree entirely rather than nest under
  // it (Gantt bars still won't render for it - that's separately gated on having real dates).
  // Untiered BAU keeps its own flat section below instead, same as always.
  const projectInitiatives = initiatives.filter((i) => !i.isBau || i.parentInitiativeId);
  const bauInitiatives = initiatives.filter((i) => i.isBau && !i.parentInitiativeId);

  const active = projectInitiatives.filter((i) => i.status !== 'Completed' && i.status !== 'Cancelled');
  const onTrack = active.filter((i) => effectiveHealth(i) === 'On Track').length;
  const atRisk = active.filter((i) => { const h = effectiveHealth(i); return h === 'At Risk' || h === 'Blocked'; }).length;

  const personName = (id: string) => people.find((p: any) => p.personId === id)?.personName || id || 'Unassigned';

  // The "All initiatives" table below shows the same Portfolio/Program/Project cascade as the
  // dedicated All Initiatives page, over every active initiative regardless of whether it has
  // dates set (unlike the Gantt above, a table row doesn't need a start/end date to make sense).
  const activeChildrenOf = useMemo(() => buildChildrenIndex(active), [active]);
  const activeRoots = useMemo(() => getRootInitiatives(active), [active]);
  const activeUnreachable = useMemo(
    () => getUnreachableInitiatives(active, activeRoots, activeChildrenOf),
    [active, activeRoots, activeChildrenOf],
  );

  const dated = useMemo(() => active.filter((i) => i.startDate && i.currentProjectedEndDate), [active]);
  const datedIds = useMemo(() => new Set(dated.map((i) => i.initiativeId)), [dated]);

  const childrenOf = useMemo(() => buildChildrenIndex(dated), [dated]);

  // Each tier button picks a different root set for the same cascade: "All" roots on whatever
  // has no parent (or a parent outside this dated set), so every Portfolio, orphan Program and
  // orphan Project shows at the top level with its own descendants nested beneath it. Picking
  // a specific tier instead roots on every initiative *at* that tier, ignoring its own parent -
  // "Program" shows every Program with its Projects nested below, even one that also belongs to
  // a Portfolio; "Project" degenerates to a flat list, since Projects have no children.
  const rows = useMemo(() => {
    const roots = tierFilter === 'All'
      ? dated.filter((i) => !i.parentInitiativeId || !datedIds.has(i.parentInitiativeId))
      : dated.filter((i) => i.tier === tierFilter);
    const sortedRoots = tierFilter === 'Project'
      ? [...roots].sort((a, b) =>
          groupBy === 'category' ? a.category.localeCompare(b.category) : personName(a.ownerId).localeCompare(personName(b.ownerId))
        )
      : [...roots].sort((a, b) => a.name.localeCompare(b.name));
    return flattenTierTree(sortedRoots, childrenOf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dated, datedIds, childrenOf, tierFilter, groupBy, people]);

  const series = [
    {
      data: rows.map(({ initiative: i, depth }) => ({
        x: indentLabel(i.name, depth),
        y: [new Date(i.startDate).getTime(), new Date(i.currentProjectedEndDate || i.originalTargetEndDate).getTime()],
      })),
    },
  ];

  // Frozen at mount rather than read fresh on every render (same convention
  // HubInitiativeTracker.tsx already follows for its own copy of this chart) - a chart
  // annotation doesn't need sub-day precision, and Date.now() is an impure call this
  // codebase's lint rules reject inside the render body.
  const [todayTs] = useState(() => Date.now());

  // Same cascading-tree row shape as AllInitiativesPage.tsx's own renderTreeRows - not shared
  // as a component since the two tables have different column sets, but the indent/chevron/
  // cycle-fallback conventions are kept identical on purpose.
  const renderActiveTreeRow = (i: Initiative, depth: number, ancestorLines: boolean[] = []): React.ReactNode => {
    const children = activeChildrenOf.get(i.initiativeId) || [];
    const hasChildren = children.length > 0;
    const isCollapsed = collapsedIds.has(i.initiativeId);
    const slug = tierSlug(i.tier);
    // Whether THIS row has a later sibling - the last entry ancestorLines carries in for it
    // - decides whether its own corner mark is a tee (continues) or an elbow (stops here).
    // Same tree-guide algorithm as AllInitiativesPage.tsx's renderTreeRows.
    const isLastChild = ancestorLines.length > 0 && !ancestorLines[ancestorLines.length - 1];
    return (
      <React.Fragment key={i.initiativeId}>
        <tr
          className={slug === 'portfolio' || slug === 'program' ? `np-tier-row-${slug}` : undefined}
          style={{ cursor: 'pointer' }}
          onClick={() => navigate(`/tracker/initiatives/${i.initiativeId}`)}
        >
          <td className={`np-tier-cell-${slug}`}>
            <div className="d-flex align-items-center">
              {ancestorLines.slice(0, -1).map((hasMore, k) => (
                <span key={k} className={`np-tree-guide${hasMore ? ' np-tree-guide-line' : ''}`}></span>
              ))}
              {depth > 0 && (
                <span className={`np-tree-guide np-tree-guide-corner${isLastChild ? ' np-tree-guide-corner-last' : ''}`}></span>
              )}
              {hasChildren ? (
                <button
                  type="button"
                  className="np-icon-btn me-1"
                  onClick={(e) => { e.stopPropagation(); toggleCollapsed(i.initiativeId); }}
                  title={isCollapsed ? 'Expand' : 'Collapse'}
                >
                  <i className={isCollapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-down-s-line'}></i>
                </button>
              ) : (
                <span style={{ display: 'inline-block', width: '1.875rem' }}></span>
              )}
              <div className={`np-tier-badge np-tier-badge-${slug}`} title={i.tier || 'No tier'}>
                <i className={i.tier ? TIER_ICON[i.tier] : 'ri-checkbox-blank-circle-line'}></i>
              </div>
              <div className="ms-2">
                <Link to={`/tracker/initiatives/${i.initiativeId}`} className="np-row-title" onClick={(e) => e.stopPropagation()}>{i.name}</Link>
                <div className="d-flex align-items-center gap-1">
                  <span className={`np-chip np-tone-${toneOf(CATEGORY_COLOR[i.category] || 'secondary')}`}>{i.category}</span>
                  {i.tier && <span className={`np-chip np-tone-${TIER_TONE[i.tier] || 'neutral'}`}>{i.tier}</span>}
                </div>
              </div>
              {hasChildren && (
                <span className="np-chip np-tone-neutral np-tabular ms-2" style={{ fontSize: '0.7rem', alignSelf: 'flex-start', marginTop: '0.1rem' }}>{children.length}</span>
              )}
            </div>
          </td>
          <td>
            <span className="d-flex align-items-center gap-2">
              <span className="avatar-init xs" style={{ background: 'color-mix(in srgb, var(--bs-paper-bg) 84%, var(--bs-secondary))', color: 'var(--bs-secondary)' }}>
                {initials(personName(i.ownerId))}
              </span>
              {personName(i.ownerId)}
            </span>
          </td>
          <td><span className={`np-chip np-tone-${toneOf(HEALTH_COLOR[effectiveHealth(i)])}`}>{effectiveHealth(i)}</span></td>
          <td>
            <div className="d-flex align-items-center gap-2">
              <div className="np-progress" style={{ width: '70px' }}><div className="np-fill" style={{ width: `${i.percentComplete}%`, background: HEALTH_HEX[effectiveHealth(i)] }}></div></div>
              <span className="np-tabular" style={{ fontSize: '11.5px' }}>{i.percentComplete}%</span>
            </div>
          </td>
          <td className="np-tabular" style={{ fontSize: '12px' }}>{fmtRelative(i.updatedAt)}</td>
          <td className="np-tabular" style={{ fontSize: '12.5px' }}>{formatDate(i.currentProjectedEndDate)}</td>
        </tr>
        {hasChildren && !isCollapsed && children.map((child, idx) =>
          renderActiveTreeRow(child, depth + 1, [...ancestorLines, idx !== children.length - 1])
        )}
      </React.Fragment>
    );
  };

  // Deliberately flat, non-recursive - see utils/initiativeHierarchy.ts and
  // AllInitiativesPage.tsx's own renderUnreachableRow for why a cyclic node must never be fed
  // back through the recursive renderer above.
  const renderActiveUnreachableRow = (i: Initiative): React.ReactNode => (
    <tr
      key={i.initiativeId}
      style={{ cursor: 'pointer', background: 'color-mix(in srgb, var(--bs-warning) 6%, var(--np-surface))' }}
      onClick={() => navigate(`/tracker/initiatives/${i.initiativeId}`)}
    >
      <td>
        <div className="d-flex align-items-center gap-2">
          <i className="ri-error-warning-line text-warning" title="Circular or broken parent reference - shown here rather than under its parent"></i>
          <Link to={`/tracker/initiatives/${i.initiativeId}`} className="np-row-title" onClick={(e) => e.stopPropagation()}>{i.name}</Link>
          <span className="np-chip np-tone-warning" style={{ fontSize: '0.7rem' }}>Broken parent link</span>
        </div>
      </td>
      <td>
        <span className="d-flex align-items-center gap-2">
          <span className="avatar-init xs" style={{ background: 'color-mix(in srgb, var(--bs-paper-bg) 84%, var(--bs-secondary))', color: 'var(--bs-secondary)' }}>
            {initials(personName(i.ownerId))}
          </span>
          {personName(i.ownerId)}
        </span>
      </td>
      <td><span className={`np-chip np-tone-${toneOf(HEALTH_COLOR[effectiveHealth(i)])}`}>{effectiveHealth(i)}</span></td>
      <td>
        <div className="d-flex align-items-center gap-2">
          <div className="np-progress" style={{ width: '70px' }}><div className="np-fill" style={{ width: `${i.percentComplete}%`, background: HEALTH_HEX[effectiveHealth(i)] }}></div></div>
          <span className="np-tabular" style={{ fontSize: '11.5px' }}>{i.percentComplete}%</span>
        </div>
      </td>
      <td className="np-tabular" style={{ fontSize: '12px' }}>{fmtRelative(i.updatedAt)}</td>
      <td className="np-tabular" style={{ fontSize: '12.5px' }}>{formatDate(i.currentProjectedEndDate)}</td>
    </tr>
  );

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <div className="np-grid np-grid-4">
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-primary"><i className="ri-rocket-line"></i></span>
            <p className="np-stat-label">Active initiatives</p>
            <p className="np-stat-value np-tabular">{active.length}</p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-success"><i className="ri-checkbox-circle-line"></i></span>
            <p className="np-stat-label">On track</p>
            <p className="np-stat-value np-tabular">{onTrack}</p>
          </div>
        </div>
        <div className={`np-card ${atRisk > 0 ? 'np-card-accent-warning' : ''}`}>
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-warning"><i className="ri-alert-line"></i></span>
            <p className="np-stat-label">At risk / blocked</p>
            <p className="np-stat-value np-tabular">{atRisk}</p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-neutral"><i className="ri-list-check-2"></i></span>
            <p className="np-stat-label">Total tracked</p>
            <p className="np-stat-value np-tabular">{projectInitiatives.length}</p>
          </div>
        </div>
      </div>

      <div className="np-card">
        <div className="np-card-head" style={{ flexWrap: 'wrap', rowGap: '0.5rem' }}>
          <h2>Timeline</h2>
          <div className="d-flex gap-2 flex-wrap" style={{ marginLeft: 'auto' }}>
            {tierFilter === 'Project' && (
              <div className="np-pill-toggle-group" role="group">
                <button className={`np-pill-toggle np-tone-primary${groupBy === 'category' ? ' active' : ''}`} onClick={() => setGroupBy('category')}>By category</button>
                <button className={`np-pill-toggle np-tone-primary${groupBy === 'owner' ? ' active' : ''}`} onClick={() => setGroupBy('owner')}>By owner</button>
              </div>
            )}
            <div className="np-pill-toggle-group" role="group">
              {TIER_FILTERS.map((t) => (
                <button key={t} className={`np-pill-toggle np-tone-primary${tierFilter === t ? ' active' : ''}`} onClick={() => setTierFilter(t)}>{t}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="np-card-body">
          {rows.length === 0 ? (
            <div className="text-center py-4">
              <i className="ri-bar-chart-2-line d-block mb-2" style={{ fontSize: '1.75rem', color: 'var(--bs-secondary-color)' }}></i>
              <p className="np-sub mb-0">
                {tierFilter === 'All'
                  ? 'No initiatives with both a start date and a projected end date yet.'
                  : `No ${tierFilter} initiatives with both a start date and a projected end date yet.`}
              </p>
            </div>
          ) : (
            <Chart
              type="rangeBar"
              height={Math.max(120, rows.length * 52)}
              series={series}
              options={{
                chart: { type: 'rangeBar', toolbar: { show: false }, animations: { enabled: false } },
                plotOptions: {
                  bar: { horizontal: true, distributed: true, barHeight: '55%', rangeBarGroupRows: true },
                },
                colors: rows.map(({ initiative: i }) => HEALTH_HEX[effectiveHealth(i)] || 'var(--bs-secondary)'),
                xaxis: { type: 'datetime', labels: { style: { colors: 'var(--bs-secondary-color)' } } },
                yaxis: { labels: { style: { colors: 'var(--bs-secondary-color)', fontSize: '12.5px' } } },
                grid: { borderColor: 'var(--bs-border-color)' },
                dataLabels: { enabled: false },
                legend: { show: false },
                tooltip: {
                  custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
                    const { initiative: i, depth } = rows[dataPointIndex];
                    return `<div style="padding:8px 12px;font-size:12.5px;">
                      <strong>${i.name}</strong>${i.tier ? ` · ${i.tier}` : ''}${depth > 0 ? ' (nested)' : ''}<br/>
                      ${effectiveHealth(i)} · ${i.percentComplete}% complete<br/>
                      ${formatDate(i.startDate)} → ${formatDate(i.currentProjectedEndDate)}
                    </div>`;
                  },
                },
                annotations: {
                  xaxis: [
                    {
                      x: todayTs,
                      borderColor: 'var(--bs-danger)',
                      label: { text: 'Today', style: { color: '#fff', background: 'var(--bs-danger)', fontSize: '10px' } },
                    },
                  ],
                },
              }}
            />
          )}
          <div className="np-legend">
            <div className="np-legend-item"><span className="np-legend-swatch" style={{ background: 'var(--bs-success)' }}></span>On track</div>
            <div className="np-legend-item"><span className="np-legend-swatch" style={{ background: 'var(--bs-warning)' }}></span>At risk</div>
            <div className="np-legend-item"><span className="np-legend-swatch" style={{ background: 'var(--bs-danger)' }}></span>Blocked</div>
            <div className="np-legend-item"><span className="np-legend-swatch" style={{ background: 'var(--bs-secondary)' }}></span>Not started</div>
          </div>
        </div>
      </div>

      <div className="np-card">
        <div className="np-card-head"><h2>All initiatives</h2></div>
        <div className="np-card-body p-0">
          <div className="np-table-wrap">
            <table className="np-table">
              <thead>
                <tr>
                  <th>Initiative</th><th>Owner</th><th>Health</th><th>Progress</th><th>Last updated</th><th>Projected end</th>
                </tr>
              </thead>
              <tbody>
                {activeRoots.map((i) => renderActiveTreeRow(i, 0))}
                {activeUnreachable.map((i) => renderActiveUnreachableRow(i))}
                {active.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-4">
                      <i className="ri-flashlight-line d-block mb-2" style={{ fontSize: '1.75rem', color: 'var(--bs-secondary-color)' }}></i>
                      <p className="np-sub mb-0">No active initiatives.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {bauInitiatives.length > 0 && (
        <div className="np-card">
          <div className="np-card-head">
            <h2>Standalone Work</h2>
            <span className="np-chip np-tone-neutral">{bauInitiatives.length}</span>
          </div>
          <div className="np-card-body p-0">
            <div className="np-table-wrap">
              <table className="np-table">
                <thead>
                  <tr><th>Initiative</th><th>Owner</th><th>Last updated</th></tr>
                </thead>
                <tbody>
                  {bauInitiatives.map((i) => (
                    <tr key={i.initiativeId} style={{ cursor: 'pointer' }} onClick={() => navigate(`/tracker/initiatives/${i.initiativeId}`)}>
                      <td>
                        <Link to={`/tracker/initiatives/${i.initiativeId}`} className="np-row-title" onClick={(e) => e.stopPropagation()}>{i.name}</Link>
                        <div><span className={`np-chip np-tone-${toneOf(CATEGORY_COLOR[i.category] || 'secondary')}`}>{i.category}</span></div>
                      </td>
                      <td>
                        <span className="d-flex align-items-center gap-2">
                          <span className="avatar-init xs" style={{ background: 'color-mix(in srgb, var(--bs-paper-bg) 84%, var(--bs-secondary))', color: 'var(--bs-secondary)' }}>
                            {initials(personName(i.ownerId))}
                          </span>
                          {personName(i.ownerId)}
                        </span>
                      </td>
                      <td className="np-tabular" style={{ fontSize: '12px' }}>{fmtRelative(i.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
